import { useState, useEffect } from 'react'
import { FiRefreshCw, FiChevronDown, FiChevronRight, FiCalendar } from 'react-icons/fi'
import toast from 'react-hot-toast'
import api from '../utils/api'
import { useAuth } from '../App'
import AlberoDetayModal from '../components/AlberoDetayModal'

const formatMoney = (v) => {
  if (v == null || isNaN(v)) return '0,00'
  return Number(v).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

export default function Plasiyer() {
  const { selectedCompanyId } = useAuth()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [tarihBas, setTarihBas] = useState(() => new Date().toISOString().slice(0, 10))
  const [tarihBit, setTarihBit] = useState(() => new Date().toISOString().slice(0, 10))
  // Birden fazla plasiyer aynı anda açık kalabilsin: ad -> { loading, data }
  const [detayMap, setDetayMap] = useState({})
  const [modal, setModal] = useState(null)

  useEffect(() => { loadData() }, [selectedCompanyId])

  const loadData = async () => {
    setLoading(true)
    setDetayMap({})  // tarih değişince açık detayları kapat (eski veri kalmasın)
    try {
      const { data: result } = await api.get('/plasiyer/ozet', {
        params: { tarih_bas: tarihBas, tarih_bit: tarihBit }
      })
      setData(result)
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Plasiyer raporu yuklenemedi')
    }
    setLoading(false)
  }

  const loadDetay = async (plasiyerAdi) => {
    // Açıksa kapat (map'ten çıkar)
    if (detayMap[plasiyerAdi]) {
      setDetayMap(m => { const n = { ...m }; delete n[plasiyerAdi]; return n })
      return
    }
    setDetayMap(m => ({ ...m, [plasiyerAdi]: { loading: true, data: null } }))
    try {
      const { data: result } = await api.get(`/plasiyer/detay/${encodeURIComponent(plasiyerAdi)}`, {
        params: { tarih_bas: tarihBas, tarih_bit: tarihBit }
      })
      setDetayMap(m => ({ ...m, [plasiyerAdi]: { loading: false, data: result } }))
    } catch (err) {
      toast.error('Detay yuklenemedi')
      setDetayMap(m => { const n = { ...m }; delete n[plasiyerAdi]; return n })
    }
  }

  const kpis = data?.kpis || {}

  return (
    <div className="p-4 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-700 rounded-xl p-4 mb-4 text-white">
        <h1 className="text-lg font-bold mb-1">Plasiyer Satis Dashboard</h1>
        <div className="flex items-center gap-3 text-sm">
          <div className="flex items-center gap-1.5">
            <FiCalendar size={13} />
            <input type="date" value={tarihBas} onChange={e => setTarihBas(e.target.value)}
              className="bg-white/10 border border-white/20 rounded px-2 py-0.5 text-xs text-white" />
            <span>-</span>
            <input type="date" value={tarihBit} onChange={e => setTarihBit(e.target.value)}
              className="bg-white/10 border border-white/20 rounded px-2 py-0.5 text-xs text-white" />
          </div>
          <button onClick={loadData} disabled={loading}
            className="flex items-center gap-1 bg-white/20 hover:bg-white/30 px-3 py-1 rounded text-xs">
            <FiRefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Yenile
          </button>
          <span className="text-xs text-white/60 ml-auto">
            {data && `${data.tarih_bas} - ${data.tarih_bit} | Plasiyer: ${kpis.toplam_plasiyer || 0}`}
          </span>
        </div>
      </div>

      {/* KPI'lar */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3 mb-4">
          {[
            { label: 'TOPLAM SATIS (KDV DAHiL)', value: formatMoney(kpis.toplam_satis), color: 'text-blue-700', border: 'border-blue-400' },
            { label: 'TOPLAM TAHSILAT', value: formatMoney(kpis.toplam_tahsilat), color: 'text-green-700', border: 'border-green-400' },
            { label: 'TOPLAM BAKIYE', value: formatMoney(kpis.toplam_bakiye), color: 'text-red-700', border: 'border-red-400' },
            { label: 'AKTIF PLASIYER', value: kpis.aktif_plasiyer || 0, color: 'text-purple-700', border: 'border-purple-400' },
            { label: 'YENI MUSTERI', value: kpis.yeni_musteri || 0, color: 'text-teal-700', border: 'border-teal-400' },
            { label: 'FIS SATISI', value: formatMoney(kpis.fis_satisi), color: 'text-orange-700', border: 'border-orange-400' },
          ].map((kpi, i) => (
            <div key={i} className={`bg-white rounded-lg border-t-3 ${kpi.border} p-3 shadow-sm`} style={{ borderTopWidth: '3px' }}>
              <div className="text-[9px] font-semibold text-gray-400 uppercase mb-1">{kpi.label}</div>
              <div className={`text-lg font-bold ${kpi.color}`}>{kpi.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Plasiyer Tablosu */}
      {data?.plasiyerler && (
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-700 text-white text-[10px]">
                <th className="px-1 py-2 w-6"></th>
                <th className="text-left px-2 py-2 font-medium">Plasiyer</th>
                <th className="text-right px-2 py-2 font-medium">Must.Ad.</th>
                <th className="text-right px-2 py-2 font-medium">Bakiye</th>
                <th className="text-right px-2 py-2 font-medium">KDV Dahil Satis</th>
                <th className="text-right px-2 py-2 font-medium">Satis</th>
                <th className="text-right px-2 py-2 font-medium">Iade</th>
                <th className="text-right px-2 py-2 font-medium">Fat.Say.</th>
                <th className="text-right px-2 py-2 font-medium">Sat.Must.</th>
                <th className="text-right px-2 py-2 font-medium">Urun Ces.</th>
                <th className="text-right px-2 py-2 font-medium">Yeni Must.</th>
                <th className="text-right px-2 py-2 font-medium">Fis Satis</th>
                <th className="text-right px-2 py-2 font-medium">Fis Ad.</th>
                <th className="text-right px-2 py-2 font-medium">Tah.Nakit</th>
                <th className="text-right px-2 py-2 font-medium">Tah.Kart</th>
                <th className="text-right px-2 py-2 font-medium">Tah.Toplam</th>
              </tr>
            </thead>
            <tbody>
              {data.plasiyerler.map((p, i) => {
                const entry = detayMap[p.PLASIYER]
                const isExpanded = !!entry
                const detayLoading = entry?.loading
                const detayData = entry?.data
                return (
                  <tbody key={i}>
                    <tr
                      onClick={() => loadDetay(p.PLASIYER)}
                      className={`cursor-pointer border-b transition-colors ${isExpanded ? 'bg-blue-50' : 'hover:bg-gray-50'} ${p.FAT_SAY === 0 ? 'text-gray-400' : ''}`}>
                      <td className="px-1 py-1.5 text-center">
                        {isExpanded ? <FiChevronDown size={11} className="text-blue-600" /> : <FiChevronRight size={11} className="text-gray-400" />}
                      </td>
                      <td className="px-2 py-1.5 font-medium">{p.PLASIYER}</td>
                      <td className="text-right px-2 py-1.5">{p.MUSTERI_AD}</td>
                      <td className={`text-right px-2 py-1.5 font-medium ${(p.BAKIYE || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {formatMoney(p.BAKIYE)}
                      </td>
                      <td className="text-right px-2 py-1.5 font-medium text-blue-700">{formatMoney(p.KDV_DAHIL_SATIS)}</td>
                      <td className="text-right px-2 py-1.5">{formatMoney(p.SATIS)}</td>
                      <td className={`text-right px-2 py-1.5 ${(p.IADE || 0) > 0 ? 'text-red-500' : ''}`}>{formatMoney(p.IADE)}</td>
                      <td className="text-right px-2 py-1.5">{p.FAT_SAY}</td>
                      <td className="text-right px-2 py-1.5">{p.SAT_MUST}</td>
                      <td className="text-right px-2 py-1.5">{p.URUN_CES || 0}</td>
                      <td className="text-right px-2 py-1.5">{p.YENI_MUST || 0}</td>
                      <td className="text-right px-2 py-1.5">{formatMoney(p.FIS_SATIS)}</td>
                      <td className="text-right px-2 py-1.5">{p.FIS_AD || 0}</td>
                      <td className="text-right px-2 py-1.5">{formatMoney(p.TAH_NAKIT)}</td>
                      <td className="text-right px-2 py-1.5">{formatMoney(p.TAH_KART)}</td>
                      <td className="text-right px-2 py-1.5 text-green-700 font-medium">{formatMoney(p.TAH_TOPLAM)}</td>
                    </tr>
                    {/* Detay açılır bölüm */}
                    {isExpanded && (
                      <tr>
                        <td colSpan={16} className="bg-slate-50 px-4 py-3">
                          {detayLoading ? (
                            <div className="text-center text-gray-400 py-4">Yukleniyor...</div>
                          ) : detayData?.islemler?.length > 0 ? (
                            <div>
                              <div className="bg-slate-700 text-white px-3 py-1.5 rounded-t text-xs font-medium">
                                {p.PLASIYER} — {data.tarih_bas} · {detayData.islemler.length} islem
                              </div>
                              <table className="w-full text-[11px] bg-white border">
                                <thead>
                                  <tr className="bg-gray-100 text-gray-600">
                                    <th className="text-left px-2 py-1">Cari</th>
                                    <th className="text-left px-2 py-1">Tip</th>
                                    <th className="text-left px-2 py-1">Evrak No</th>
                                    <th className="text-right px-2 py-1">Tutar</th>
                                    <th className="text-left px-2 py-1">Aciklama</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {detayData.islemler.map((islem, j) => {
                                    const isTah = islem.KATEGORI === 'TAHSILAT'
                                    const tipColor = isTah ? 'text-green-600'
                                      : (islem.TIP || '').includes('iad') ? 'text-red-600'
                                      : 'text-blue-600'
                                    const openIslem = () => isTah
                                      ? setModal({ type: 'tahsilat', blcari: islem.BLCARI, evrak_no: islem.EVRAK_NO })
                                      : setModal({ type: 'fatura', fatno: islem.FATNO })
                                    return (
                                      <tr key={j} className="border-b border-gray-50 hover:bg-blue-50/30">
                                        <td className="px-2 py-1 text-blue-600 font-medium cursor-pointer hover:underline"
                                          onClick={() => setModal({ type: 'cari', blcari: islem.BLCARI, unvan: islem.CARI })}>{islem.CARI}</td>
                                        <td className={`px-2 py-1 font-medium cursor-pointer hover:underline ${tipColor}`} onClick={openIslem}>{islem.TIP}</td>
                                        <td className="px-2 py-1 text-gray-500 cursor-pointer" onClick={openIslem}>{islem.EVRAK_NO}</td>
                                        <td className="text-right px-2 py-1 font-medium cursor-pointer" onClick={openIslem}>{formatMoney(islem.TUTAR)}</td>
                                        <td className="px-2 py-1 text-gray-400 truncate max-w-[200px]">{islem.ACIKLAMA}</td>
                                      </tr>
                                    )
                                  })}
                                </tbody>
                                <tfoot>
                                  <tr className="bg-red-700 text-white font-bold">
                                    <td colSpan={3} className="px-2 py-1.5 text-right">PLASIYER TOPLAM</td>
                                    <td className="text-right px-2 py-1.5">
                                      {formatMoney(detayData.islemler.reduce((s, r) => s + (r.TUTAR || 0), 0))}
                                    </td>
                                    <td></td>
                                  </tr>
                                </tfoot>
                              </table>
                            </div>
                          ) : (
                            <div className="text-center text-gray-400 py-3">Bu tarihte islem yok</div>
                          )}
                        </td>
                      </tr>
                    )}
                  </tbody>
                )
              })}
              {/* TOPLAM satırı */}
              {(() => {
                const t = (key) => data.plasiyerler.reduce((s, p) => s + (p[key] || 0), 0)
                return (
              <tr className="bg-slate-100 font-bold text-xs border-t-2 border-slate-300">
                <td className="px-1 py-2"></td>
                <td className="px-2 py-2">TOPLAM</td>
                <td className="text-right px-2 py-2">{t('MUSTERI_AD')}</td>
                <td className="text-right px-2 py-2">{formatMoney(t('BAKIYE'))}</td>
                <td className="text-right px-2 py-2 text-blue-700">{formatMoney(t('KDV_DAHIL_SATIS'))}</td>
                <td className="text-right px-2 py-2">{formatMoney(t('SATIS'))}</td>
                <td className="text-right px-2 py-2 text-red-500">{formatMoney(t('IADE'))}</td>
                <td className="text-right px-2 py-2">{t('FAT_SAY')}</td>
                <td className="text-right px-2 py-2">{t('SAT_MUST')}</td>
                <td className="text-right px-2 py-2">{t('URUN_CES')}</td>
                <td className="text-right px-2 py-2">{t('YENI_MUST')}</td>
                <td className="text-right px-2 py-2">{formatMoney(t('FIS_SATIS'))}</td>
                <td className="text-right px-2 py-2">{t('FIS_AD')}</td>
                <td className="text-right px-2 py-2">{formatMoney(t('TAH_NAKIT'))}</td>
                <td className="text-right px-2 py-2">{formatMoney(t('TAH_KART'))}</td>
                <td className="text-right px-2 py-2 text-green-700">{formatMoney(t('TAH_TOPLAM'))}</td>
              </tr>
                )
              })()}
            </tbody>
          </table>
        </div>
      )}

      {!data && !loading && (
        <div className="text-center text-gray-400 py-20">Tarih secin ve "Yenile" butonuna basin</div>
      )}

      {modal && <AlberoDetayModal initial={modal} onClose={() => setModal(null)} />}
    </div>
  )
}
